import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Search, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lead } from '@/types/lead';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'CEO' | 'GROUP_MANAGER' | 'MANAGER' | 'EXECUTIVE';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  department?: string;
  position?: string;
}

interface ReassignLeadModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  onReassign: (newOwnerId: string, note?: string) => Promise<void>;
}

export const ReassignLeadModal: React.FC<ReassignLeadModalProps> = ({
  open,
  onClose,
  lead,
  onReassign
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch all active users
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        user.department?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      setIsFetchingUsers(true);
      setError(null);

      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();

      // Handle response structure: { success: true, data: [...] } or direct array
      const usersArray = data.success && data.data ? data.data : (data.users || data);

      // Filter only active users
      const activeUsers = (Array.isArray(usersArray) ? usersArray : []).filter(
        (user: User) => user.status === 'ACTIVE'
      );

      setUsers(activeUsers);
      setFilteredUsers(activeUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsFetchingUsers(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setError(null);
  };

  const handleReassign = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    // Validate note length
    if (note.length > 500) {
      setError('Note cannot exceed 500 characters');
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const confirmReassignment = async () => {
    if (!selectedUserId) return;

    try {
      setIsLoading(true);
      setError(null);

      await onReassign(selectedUserId, note.trim() || undefined);

      // Success - close modal
      handleClose();
    } catch (err: any) {
      console.error('Error reassigning lead:', err);
      setError(err.message || 'Failed to reassign lead. Please try again.');
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId(null);
    setNote('');
    setSearchQuery('');
    setError(null);
    setShowConfirmation(false);
    onClose();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'CEO':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'GROUP_MANAGER':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'MANAGER':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'EXECUTIVE':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    return role.replace('_', ' ');
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="bg-white shadow-2xl h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Reassign Lead</h2>
                    <p className="text-sm text-gray-600">{lead.name} - {lead.company}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Current Owner */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-1">Current Owner</p>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {lead.assignedToUser?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {lead.assignedToUser?.name || 'Unassigned'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {lead.assignedToUser?.email || 'No owner'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {!showConfirmation ? (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by name, email, or role..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        disabled={isFetchingUsers || isLoading}
                      />
                    </div>
                  </div>

                  {/* User List */}
                  <div className="space-y-2 mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Select New Owner ({filteredUsers.length} users)
                    </p>

                    {isFetchingUsers ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                        <p className="text-sm text-gray-500 mt-2">Loading users...</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500">
                          {searchQuery ? 'No users found matching your search' : 'No active users available'}
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                        {filteredUsers.map((user) => {
                          const isCurrentOwner = user.id === lead.assignedToUser?.id;
                          const isSelected = user.id === selectedUserId;

                          return (
                            <button
                              key={user.id}
                              onClick={() => handleSelectUser(user.id)}
                              disabled={isCurrentOwner || isLoading}
                              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : isCurrentOwner
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 ${
                                  isSelected ? 'bg-blue-500' : 'bg-gray-400'
                                } rounded-full flex items-center justify-center flex-shrink-0`}>
                                  <span className="text-white text-sm font-medium">
                                    {user.name.charAt(0)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {user.name}
                                    </p>
                                    {isCurrentOwner && (
                                      <Badge variant="outline" className="text-xs">
                                        Current
                                      </Badge>
                                    )}
                                    {isSelected && (
                                      <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <Badge className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                                      {getRoleLabel(user.role)}
                                    </Badge>
                                    {user.department && (
                                      <span className="text-xs text-gray-400">{user.department}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Optional Note */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Reassignment Note (Optional)
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Add a note about why this lead is being reassigned..."
                      rows={3}
                      maxLength={500}
                      disabled={isLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {note.length}/500 characters
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                </>
              ) : (
                /* Confirmation Screen */
                <div className="py-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="h-8 w-8 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Confirm Lead Reassignment
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to reassign this lead?
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Lead</p>
                      <p className="text-sm font-medium text-gray-900">
                        {lead.name} - {lead.company}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500">From</p>
                        <p className="text-sm font-medium text-gray-900">
                          {lead.assignedToUser?.name || 'Unassigned'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {lead.assignedToUser?.email || 'No owner'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-500">To</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedUser?.name}
                        </p>
                        <p className="text-xs text-gray-500">{selectedUser?.email}</p>
                      </div>
                    </div>

                    {note && (
                      <div>
                        <p className="text-xs font-medium text-gray-500">Note</p>
                        <p className="text-sm text-gray-700">{note}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {!showConfirmation ? (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReassign}
                    disabled={!selectedUserId || isLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Reassign Lead
                  </Button>
                </div>
              ) : (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmation(false)}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={confirmReassignment}
                    disabled={isLoading}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Reassigning...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Confirm Reassignment
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
